import { inject, Injectable } from '@angular/core';
import { QuoteService } from '@lx/quote-tool/shared/data-access/quote';
import { AnalyticsService } from '@lx/quote-tool/shared/services';
import { QuoteLeadInputV5, QuoteLeadOriginEnum, QuoteLeadOutputV5, QuoteLeadQuoteInput, StudentDetails } from '@lx/quote-tool/shared/types';
import { getLocale } from '@lx/shared/i18n';
import { LoggerService } from '@lx/shared/logger';
import { getDateStringFromDate, getToday } from '@lx/shared/utils';
import { Observable, throwError, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ClassTimeTimezoneDefaultsService } from '../services';
import { OnlineQuoteToolState } from '../store';
import { LeadUserDataPayload } from '../types';
import { OnlineQuoteInputMapper } from './quote-input.mapper';

@Injectable({
  providedIn: 'root',
})
export class LeadService {
  private readonly onlineLeadType = 'Online Course Lead';

  private readonly quoteService = inject(QuoteService);
  private readonly quoteInputMapper = inject(OnlineQuoteInputMapper);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly loggerService = inject(LoggerService);
  private readonly classTimeTimezoneDefaults = inject(ClassTimeTimezoneDefaultsService);
  private readonly locale = getLocale();

  createLead(state: Partial<OnlineQuoteToolState>): Observable<QuoteLeadOutputV5> {
    return this.buildLeadPayload(state).pipe(
      switchMap((payload) => {
        if (!payload) {
          return throwError(() => new Error('Unable to build lead payload from current online selections'));
        }
        return this.quoteService.createLeadV5(payload);
      }),
    );
  }

  private buildLeadPayload(state: Partial<OnlineQuoteToolState>): Observable<QuoteLeadInputV5 | null> {
    if (!state.selectedSchool) {
      this.loggerService.error('Lead payload cannot be built without selected school');
      return of(null);
    }

    const quoteInput = this.quoteInputMapper.mapQuoteInput(state);
    if (!quoteInput) {
      this.loggerService.error('Lead payload cannot be built without valid quote input');
      return of(null);
    }

    // Get browser timezone and build payload
    return this.classTimeTimezoneDefaults.getBrowserTimezoneName$().pipe(
      switchMap((timezoneName) => {
        const defaultClassTime = this.classTimeTimezoneDefaults.getDefaultClassTimeLabel();

        const enrichedStudentDetails = {
          ...state.studentDetails,
          timeZone: timezoneName,
          preferredClassTime: defaultClassTime,
        };

        const leadUserData = this.buildLeadUserData(enrichedStudentDetails, state.residenceCountryCode, timezoneName, defaultClassTime);

        return of({
          analyticsData: this.analyticsService.getQuoteLeadAnalyticsData(),
          leadUserData,
          formOrigin: QuoteLeadOriginEnum.ONLINE_COURSES_ENROLMENT,
          simplifiedFlow: true,
          quote: this.mapStateToQuote(state, quoteInput, defaultClassTime),
        } as QuoteLeadInputV5);
      }),
    );
  }

  private mapStateToQuote(
    state: Partial<OnlineQuoteToolState>,
    quoteInput: NonNullable<ReturnType<OnlineQuoteInputMapper['mapQuoteInput']>>,
    preferredClassTime: string | undefined,
  ): QuoteLeadQuoteInput {
    const { selectedSchool, selectedCourse, selectedStartDate, selectedEndDate, currency, selectedDuration } = state;

    return {
      headerData: {
        language: this.locale,
        school: selectedSchool,
        schoolCode: selectedSchool,
        course: selectedCourse?.name,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        selectedCurrency: currency,
        bookingDate: getDateStringFromDate(getToday()),
        duration: selectedDuration,
        classTimes: preferredClassTime ? [preferredClassTime] : undefined,
      },
      quoteInput,
    };
  }

  private buildLeadUserData(
    studentDetails: StudentDetails | undefined,
    residenceCountryCode: string | undefined,
    timezoneName: string | undefined,
    preferredClassTime: string | undefined,
  ): LeadUserDataPayload {
    const { title, firstName, lastName, email, phoneNumber, countryOfResidence, consentMarketing, termsAndConditionsConsent } = studentDetails ?? {};

    return {
      title,
      firstName,
      lastName,
      email,
      phoneNumber,
      countryOfResidence: countryOfResidence || residenceCountryCode,
      leadType: this.onlineLeadType,
      timeZone: timezoneName,
      preferredClassTime,
      websiteLanguage: this.locale,
      consentMarketing,
      termsAndConditionsConsent,
    };
  }
}
